import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Box, Typography } from "@mui/material";
import AnimatedLogoWithIcons from "./AnimatedLogoWithIcons";

interface LoadingOverlayProps {
  onComplete: () => void;
  messages?: string[];
}

const defaultMessages = [
  "Excelente la idea que has elegido. Estoy procesando tu solicitud y te propondré tres ideas de proyectos posibles para trabajar con tus estudiantes.",
  "Para cada una de las tres ideas, estoy diseñando una problemática, una pregunta guía y sus productos finales.",
  "Estoy considerando el contexto para describir tres posibles problemáticas a abordar. La problemática es el motor que dinamiza el proyecto, proporcionando un contexto real y relevante para que los estudiantes apliquen sus conocimientos y habilidades.",
  "Estoy trabajando en proponerte tres opciones para la pregunta guía: la pregunta guía es nuestra herramienta más poderosa para diseñar experiencias de aprendizaje auténticas y desafiantes que promueven el pensamiento crítico y la resolución de problemas.",
  "Estoy afinando detalles para los productos finales de cada idea. Los productos finales proporcionan un sentido de logro y satisfacción a los estudiantes, motivándolos a seguir aprendiendo y mejorando.",
  "Para cada idea te entregaré información sobre los beneficiarios, storytelling, conceptos claves y aprendizajes esperados.",
  "Recuerda que yo estoy trabajando en propuestas, tú eres quien tiene la palabra final para decidir qué utilizar. Recuerda que puedes editar haciendo click en el lápiz y yo consideraré tus edificaciones para la siguiente etapa.",
  "Estoy integrando todos los elementos pedagógicos para crear una experiencia de aprendizaje coherente y significativa.",
  "Estoy validando que cada propuesta esté alineada con los objetivos de aprendizaje y el contexto de tus estudiantes.",
  "Ya estoy casi listo. ¡Juntos vamos a lograr una propuesta de aprendizaje basado en proyectos ajustada a tu contexto!"
];

export default function LoadingOverlay({
  onComplete,
  messages = defaultMessages,
}: LoadingOverlayProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentCycle, setCurrentCycle] = useState(0);
  const totalCycles = 10;

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setTimeout(() => {
            if (currentCycle >= totalCycles - 1) {
              onComplete();
            } else {
              setCurrentCycle((c) => c + 1);
              setCurrentMessageIndex((m) =>
                Math.min(m + 1, messages.length - 1)
              );
              setProgress(0);
            }
          }, 700);
          return 100;
        }
        return prev + 100 / 40; 
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [onComplete, currentCycle, messages.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(252,252,252,0.96)",
        backdropFilter: "blur(6px)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          maxWidth: 900,
          px: 4,
        }}
      >
        <AnimatedLogoWithIcons
          progress={progress}
          currentCycle={currentCycle}
          totalCycles={totalCycles}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={currentMessageIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            style={{
              textAlign: "center",
              minHeight: 120,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: "#336B87",
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                lineHeight: 1.6,
                fontWeight: 500,
                fontFamily: "Montserrat",
              }}
            >
              {messages[currentMessageIndex]}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>
    </motion.div>
  );
}
